import {
  BestTicketBlockDetails,
  EventAvailabilityDetails,
  PremiumClassDetails,
  SectionDetails,
  TenantOptionsDetails,
  TicketDetails,
} from "features/types";

export function isSectionDetails(
  section: BestTicketBlockDetails | SectionDetails,
): section is SectionDetails {
  return (section as SectionDetails).minPlanTicketCharge !== undefined;
}

function getMax(arr: BestTicketBlockDetails[]) {
  return Math.max.apply(
    Math,
    arr.map(function (o) {
      return o.quality;
    }),
  );
}
function getMin(arr: BestTicketBlockDetails[]) {
  return Math.min.apply(
    Math,
    arr.map(function (o) {
      return o.quality;
    }),
  );
}
function divideAccessibleAndInaccessibleBlocks(
  filteredBlocks: BestTicketBlockDetails[] | SectionDetails[],
) {
  const accessibleBlocks: any[] = [];
  const inaccessibleBlocks: any[] = [];
  filteredBlocks.forEach((block) => {
    block.accessible
      ? accessibleBlocks.push(block)
      : inaccessibleBlocks.push(block);
  });
  return [accessibleBlocks, inaccessibleBlocks];
}

export function rankScore(
  filteredBlocks: BestTicketBlockDetails[] | SectionDetails[],
  value: number,
  seatBlock: BestTicketBlockDetails | SectionDetails,
  item: "score" | "color" | "message",
) {
  const isAccessible = seatBlock.accessible;
  const [accessibleBlocks, inaccessibleBlocks] =
    divideAccessibleAndInaccessibleBlocks(filteredBlocks);
  const arr = isAccessible ? accessibleBlocks : inaccessibleBlocks;
  let max, min;

  max = getMax(arr);
  min = getMin(arr);

  if (max === min) return;
  let rs = Number(
    (((max - min - (value - min)) / (max - min)) * 10).toPrecision(2),
  );
  let color;
  let msg;
  if (rs === 10) {
    color = "#3498db";
    msg = "Best Seats Available! ";
  } else if (rs < 10 && rs >= 8) {
    color = "#69b34c";
    msg = "Good Seats ";
  } else if (rs < 8 && rs >= 3) {
    color = "#79ca59";
    msg = "";
  } else if (rs < 3 && rs >= 1) {
    color = "#fab733";
    msg = "";
  } else if (rs < 1) {
    color = "#ff8e15";
    msg = "Better Seats Available";
    rs = 1;
  }
  if (item == "score") {
    return rs;
  } else if (item == "color") {
    return color;
  } else if (item == "message") {
    return msg;
  }
}

export function sortBlocksFunc(
  currentBlock: BestTicketBlockDetails | SectionDetails,
  nextBlock: BestTicketBlockDetails | SectionDetails,
  sortBy: TenantOptionsDetails["defaultSeatSortBy"],
) {
  const currentPerTicketCharge = isSectionDetails(currentBlock)
    ? currentBlock.minPlanTicketCharge
    : currentBlock.perTicketCharge;
  const nextPerTicketCharge = isSectionDetails(nextBlock)
    ? nextBlock.minPlanTicketCharge
    : nextBlock.perTicketCharge;

  const { quality: currentQuality } = currentBlock;
  const { quality: nextQuality } = nextBlock;

  switch (sortBy) {
    case "PRICE_HIGH_TO_LOW":
      return -(Number(currentPerTicketCharge) - Number(nextPerTicketCharge));
    case "PRICE_LOW_TO_HIGH":
      return Number(currentPerTicketCharge) - Number(nextPerTicketCharge);
    // the lower the quality, the better(differ from UI)
    case "QUALITY_LOW_TO_HIGH":
      return Number(nextQuality) - Number(currentQuality);
    case "QUALITY_HIGH_TO_LOW":
      return Number(currentQuality) - Number(nextQuality);
    default:
      return -1;
  }
}

// if showPremium flag is true, return true
// if showPremium flag is false, then filter all sections or blocks that have a ticket charge
export function filterPremium(
  sectionOrBlock: BestTicketBlockDetails | SectionDetails,
  showPremium: boolean,
) {
  if (showPremium === true) {
    return true;
  }

  if (isSectionDetails(sectionOrBlock)) {
    return Number(sectionOrBlock.minPlanTicketCharge) === 0;
  } else {
    return Number(sectionOrBlock.perTicketCharge) === 0;
  }
}

// if the include accessible checkbox is checked, show only accessible sections/blocks
// if it is unchecked, show only non-accessible sections/blocks
export function filterAccessible(
  sectionOrBlock: BestTicketBlockDetails | SectionDetails,
  showAccessible: boolean,
) {
  return Boolean(sectionOrBlock.accessible) === showAccessible;
}

// if user is changing seats, then we don't allow change within the same row
// https://github.com/FanRally/fanrally-core/issues/1037#issuecomment-1808809147
export function filterChangingSeatsSectionRow(
  sectionOrBlock: BestTicketBlockDetails | SectionDetails,
  isChangingSeats: boolean,
  originalTickets: TicketDetails[],
) {
  // Allow updating seats within the same row if the section is GA
  if (sectionOrBlock.ga) return true;
  // SectionDetails does not have a row, so include this section
  if (isSectionDetails(sectionOrBlock)) return true;
  // If user isn't updating reservation, then include this block
  if (!isChangingSeats) return true;
  // If user is updating reservation, include this block only if it doesn't have the same row
  // as the original row
  const { section: originalSection, row: originalRow } =
    originalTickets[0].seat;
  const { section: currentSection, row: currentRow } = sectionOrBlock;

  if (originalTickets.length > 0) {
    // Do not include bestBlock if its section and row are the same as the original section and row
    if (currentSection === originalSection && currentRow === originalRow) {
      return false;
    }
  }

  return true;
}

export const getPremiumImageUrls = (
  premiumClass: PremiumClassDetails,
  event: EventAvailabilityDetails,
) => {
  if (premiumClass.premiumImageUrls.length > 0) {
    return premiumClass.premiumImageUrls;
  }
  return [event.imageUrl!];
};

// Merge blocks that share the same section + row into a single block so the UI
// renders one row entry even when availability is split into multiple seat runs
// (e.g. seats 1-5 and 10-16 of the same row). Tickets are appended in block
// order — the first (better-ranked) block's seats stay first so auto-selection
// still picks a contiguous run — and deduped by seatId.
export function mergeBySectionRow(
  items?: BestTicketBlockDetails[],
): BestTicketBlockDetails[] {
  if (!items) return [];
  const keyOf = (it: BestTicketBlockDetails) => `${it.section}|${it.row}`;
  const map = new Map<string, BestTicketBlockDetails>();

  for (const item of items) {
    const key = keyOf(item);
    const existing = map.get(key);
    if (!existing) {
      map.set(key, { ...item, tickets: [...(item.tickets || [])] });
    } else {
      const seenSeatIds = new Set(existing.tickets.map((t) => t.seatId));
      existing.tickets.push(
        ...(item.tickets || []).filter((t) => !seenSeatIds.has(t.seatId)),
      );
    }
  }

  return Array.from(map.values()).map((b) => ({
    ...b,
    blockSize: b.tickets.length,
  }));
}
